import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { applyRemoteContent, contentDefaults, contentKeys, getContentSnapshot } from '../content';
import { validateEntries } from '../content/validate';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const ContentContext = createContext(null);
const clone = (value) => JSON.parse(JSON.stringify(value));

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(() => getContentSnapshot());
  const [revision, setRevision] = useState(0);
  const [status, setStatus] = useState(isSupabaseConfigured ? 'loading' : 'offline');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [versions, setVersions] = useState({});
  const requestVersion = useRef(0);

  const apply = useCallback((next) => {
    const snapshot = applyRemoteContent(next);
    // Preserve references for unchanged documents, so background syncs do not
    // reset editors or their cursor positions.
    setContent((previous) => Object.fromEntries(contentKeys.map((key) => [key,
      JSON.stringify(previous[key]) === JSON.stringify(snapshot[key]) ? previous[key] : snapshot[key]
    ])));
    setRevision((value) => value + 1);
    return snapshot;
  }, []);

  const refresh = useCallback(async () => {
    if (!supabase) return getContentSnapshot();
    const request = ++requestVersion.current;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    let result;
    try { result = await supabase.from('cms_content').select('key, value, updated_at').abortSignal(controller.signal); }
    finally { clearTimeout(timer); }
    const { data, error } = result;
    if (error) throw error;
    if (request !== requestVersion.current) return getContentSnapshot();
    const records = Object.fromEntries((data || []).map((row) => [row.key, row.value]));
    setVersions(Object.fromEntries((data || []).map((row) => [row.key, row.updated_at])));
    const snapshot = apply(records);
    setLastSyncedAt((data || []).map((row) => row.updated_at).filter(Boolean).sort().pop() || null);
    setStatus('ready');
    return snapshot;
  }, [apply]);

  useEffect(() => {
    if (!supabase) return undefined;
    const sync = () => refresh().catch(() => setStatus('offline'));
    sync();
    const channel = supabase.channel('kriams-cms-content')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'cms_content' }, sync).subscribe();
    const refreshWhenVisible = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    // Also recover if realtime is temporarily disconnected.
    const timer = window.setInterval(refreshWhenVisible, 60000);
    return () => {
      requestVersion.current += 1;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      supabase.removeChannel(channel);
    };
  }, [refresh]);

  const saveMany = useCallback(async (entries, expectedVersions = versions) => {
    if (!supabase) throw new Error('Studio is not connected yet.');
    const errors = validateEntries(entries, getContentSnapshot());
    if (errors.length) throw new Error(errors.join('\n'));
    const values = clone(entries);
    const { data, error } = await supabase.rpc('publish_cms_content', {
      entries: values,
      expected_versions: Object.fromEntries(Object.keys(values).map((key) => [key, expectedVersions[key] || null]))
    });
    if (error) throw error;
    requestVersion.current += 1;
    apply({ ...getContentSnapshot(), ...values });
    setVersions((previous) => ({ ...previous, ...Object.fromEntries((data || []).map((row) => [row.key, row.updated_at])) }));
    setLastSyncedAt(new Date().toISOString());
    setStatus('ready');
  }, [apply, versions]);

  const save = useCallback((key, value, expectedVersions) => saveMany({ [key]: value }, expectedVersions), [saveMany]);

  const seedDefaults = useCallback(async () => {
    if (!supabase) throw new Error('Studio is not connected yet.');
    const { error } = await supabase.from('cms_content').upsert(
      contentKeys.map((key) => ({ key, value: clone(contentDefaults[key]) })),
      { onConflict: 'key', ignoreDuplicates: true }
    );
    if (error) throw error;
    return refresh();
  }, [refresh]);

  const value = useMemo(() => ({ content, revision, status, lastSyncedAt, versions, refresh, save,
    saveMany, seedDefaults, isConnected: isSupabaseConfigured
  }), [content, revision, status, lastSyncedAt, versions, refresh, save, saveMany, seedDefaults]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => {
  const value = useContext(ContentContext);
  if (!value) throw new Error('useContent must be used inside ContentProvider.');
  return value;
};
